import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@loop3/supabase-client'
import { teamMeetingBaseline } from '@loop3/meetings-core'
import { getClientBuilding } from '@/lib/api/lupe'

const MEETINGS_SHOWN = 5
const MEETINGS_SCANNED = 20

export interface ClientMeeting {
  id: string
  title: string
  starts_at: string
  tldr: string | null
  decisions: { text: string }[]
}

export interface ClientMeetings {
  recent: ClientMeeting[]
  lastMeetingAt: string | null
  openActionItems: number
}

/**
 * Past team-visible meetings with TL;DR and decisions, on the same
 * `teamMeetingBaseline` the Meetings app uses for client views. Users outside the
 * Meetings cohort get no rows from RLS, so this resolves empty for them.
 */
export function useClientMeetings(clientId: string | undefined) {
  return useQuery({
    queryKey: ['client-meetings', clientId],
    queryFn: async (): Promise<ClientMeetings> => {
      const db = supabase.schema('meetings')
      const feed = await teamMeetingBaseline(
        db
          .from('meeting_feed')
          .select('id, title, starts_at')
          .eq('client_id', clientId!)
      )
        .lte('starts_at', new Date().toISOString())
        .order('starts_at', { ascending: false })
        .limit(MEETINGS_SCANNED)
      if (feed.error) throw feed.error
      const meetings = (feed.data ?? []) as Omit<
        ClientMeeting,
        'tldr' | 'decisions'
      >[]
      if (meetings.length === 0)
        return { recent: [], lastMeetingAt: null, openActionItems: 0 }

      const shown = meetings.slice(0, MEETINGS_SHOWN)
      const [summaries, actions] = await Promise.all([
        db
          .from('meeting_summaries')
          .select('meeting_id, tldr, decisions')
          .in(
            'meeting_id',
            shown.map((m) => m.id)
          ),
        db
          .from('action_items')
          .select('id', { count: 'exact', head: true })
          .in(
            'meeting_id',
            meetings.map((m) => m.id)
          )
          .eq('state', 'open')
          .is('set_aside_at', null),
      ])
      if (summaries.error) throw summaries.error
      if (actions.error) throw actions.error

      const byId = new Map(
        (summaries.data ?? []).map((s) => [s.meeting_id as string, s])
      )
      return {
        recent: shown.map((m) => {
          const s = byId.get(m.id)
          return {
            ...m,
            tldr: (s?.tldr as string | undefined) ?? null,
            decisions: (s?.decisions as { text: string }[] | undefined) ?? [],
          }
        }),
        lastMeetingAt: meetings[0].starts_at,
        openActionItems: actions.count ?? 0,
      }
    },
    enabled: !!clientId,
    staleTime: 60_000,
  })
}

/** Lupe's summary of what we're building for the client; Lupe caches it for a few hours. */
export function useClientBuilding(prefix: string | undefined) {
  const { getToken } = useAuth()
  return useQuery({
    queryKey: ['lupe', 'client-building', prefix],
    queryFn: async () => getClientBuilding((await getToken()) ?? '', prefix!),
    enabled: !!prefix,
    staleTime: 10 * 60_000,
    retry: false,
  })
}
